import { create } from "zustand";
import type { VehicleType } from "~/lib/constants/vehicle";
import type { RetrievedPlace } from "~/lib/mapbox/mapbox";
import { resolveSearchWindow } from "~/lib/utils/datetime";
import { loadTimeZoneTable, timeZoneForCoords } from "~/lib/utils/timeZone";

/**
 * The in-progress search on `/search`, shared by the three editors that can
 * change it — the nav pill is rendered twice (lg+ in header row 1, md–lg in
 * row 2, so they can't be one node) and the mobile sheet is a third.
 *
 * A store rather than context because they aren't in one subtree: the pills sit
 * inside `<header>` and the sheet is its own sibling, and only the search-page
 * chrome reads `useSearchParams`, so a provider high enough to wrap all three
 * would pull the whole nav into its Suspense boundary.
 */
export interface SearchDraft {
  q: string;
  place: RetrievedPlace | null;
  starts_at: string;
  ends_at: string;
  vehicleSize: VehicleType | null;
}

export interface SearchDraftStore extends SearchDraft {
  hydratedKey: string | null;
  /** Mounted editors. The draft is scoped to their lifetime — see `release`. */
  editors: number;
  setLocation: (q: string) => void;
  /**
   * Commits a *resolved* place, so it re-resolves the window against the new
   * clock. Non-nullable on purpose: it used to accept `null`, and that branch
   * re-resolved the window against the viewer's clock — which could pull the
   * driver's chosen start forward as a side effect of clearing a destination,
   * nothing like the zone change the re-resolution exists for. No caller ever
   * passed one (both retrieval sites bail before calling this when the lookup
   * comes back empty), so the branch was unreachable rather than merely unused.
   * Clearing is `setLocation`'s job — typing invalidates the coordinates — or
   * `revertLocation`'s; neither touches the window.
   */
  setPlace: (place: RetrievedPlace) => void;
  setStartsAt: (starts_at: string) => void;
  setEndsAt: (ends_at: string) => void;
  setVehicleSize: (vehicleSize: VehicleType | null) => void;
  /** Seed from URL params. No-ops when `key` matches the last seed. */
  hydrate: (key: string, draft: SearchDraft) => void;
  /** Seed unconditionally — for an editor reopening on an unchanged URL. */
  reseed: (key: string, draft: SearchDraft) => void;
  /** Re-resolve the window against a new slot. Idempotent. */
  reconcile: (now: Date) => void;
  revertLocation: (fallback: {
    q: string;
    place: RetrievedPlace | null;
  }) => void;
  retain: () => void;
  release: () => void;
}

/**
 * The clock this draft's wall clocks are picked on — the destination's, once one
 * is resolved. Read off the draft rather than passed in, so every path that
 * resolves the window below floors "now" at the same place the driver is
 * searching, and `DateTimeFields` (which derives it the same way) can't offer a
 * slot the store would then pull forward.
 */
function zoneOf(draft: Pick<SearchDraft, "place">): string | undefined {
  return timeZoneForCoords(draft.place?.lat, draft.place?.lng);
}

function seed(draft: SearchDraft, key: string) {
  return {
    ...draft,
    ...resolveSearchWindow(draft, new Date(), zoneOf(draft)),
    hydratedKey: key,
  };
}

export const useSearchDraftStore = create<SearchDraftStore>((set) => ({
  q: "",
  place: null,
  starts_at: "",
  ends_at: "",
  vehicleSize: null,
  hydratedKey: null,
  editors: 0,

  // Typing invalidates the resolved place: its coordinates no longer match.
  setLocation: (q) => set({ q, place: null }),

  // A new place is usually a new clock, so the window is re-resolved against it.
  // Without this, moving a search from New York to Los Angeles would keep a start
  // that is three hours into the past there — offered by no picker, but sitting in
  // the draft and committed by the next Search.
  //
  // Only ever a resolved place, so the re-resolution always has a destination
  // clock to aim at — see the type above for why `null` is not accepted.
  // Two phases because the zone now comes from a lazily-fetched table (see
  // `loadTimeZoneTable`). The place lands immediately so the field reflects the
  // choice, and the window re-resolves once the table can name the destination's
  // clock — resolving it now, against no zone, would write a viewer-clock start
  // into the draft and commit it on the next Search. In practice the table has
  // been in memory since the form mounted, so the second phase is a microtask;
  // the guard is for the case where it isn't.
  setPlace: (place) => {
    set({ place });
    void loadTimeZoneTable().then(() => {
      set((prev) =>
        // A newer place won the race — its own resolution is authoritative.
        prev.place === place
          ? resolveSearchWindow(prev, new Date(), zoneOf(prev))
          : prev,
      );
    });
  },
  setVehicleSize: (vehicleSize) => set({ vehicleSize }),

  setStartsAt: (starts_at) =>
    set((prev) =>
      resolveSearchWindow({ ...prev, starts_at }, new Date(), zoneOf(prev)),
    ),
  setEndsAt: (ends_at) =>
    set((prev) =>
      resolveSearchWindow({ ...prev, ends_at }, new Date(), zoneOf(prev)),
    ),

  // Keyed so three editors reacting to the same param change seed once.
  hydrate: (key, draft) =>
    set((prev) => (prev.hydratedKey === key ? prev : seed(draft, key))),

  reseed: (key, draft) => set(seed(draft, key)),

  // Same object back when nothing moved, so no editor re-renders.
  reconcile: (now) =>
    set((prev) => {
      const next = resolveSearchWindow(prev, now, zoneOf(prev));
      return next.starts_at === prev.starts_at && next.ends_at === prev.ends_at
        ? prev
        : next;
    }),

  // Text and place revert together or not at all. Keeping half-typed text while
  // restoring the committed coordinates would leave the pill labelled with one
  // destination and searching another. A resolved place means the edit finished,
  // so there is nothing to revert.
  //
  // One update rather than two: reverting the text via setLocation would clear
  // the place it is restoring alongside it.
  revertLocation: (fallback) =>
    set((prev) => {
      if (prev.place) return prev;
      return prev.q === fallback.q && prev.place === fallback.place
        ? prev
        : { q: fallback.q, place: fallback.place };
    }),

  retain: () => set((prev) => ({ editors: prev.editors + 1 })),

  // The draft is an editing session, not a cache. Once the last editor unmounts
  // — navigating off /search, or back to it — the next mount must re-read the
  // URL, or an abandoned edit would come back as if it had been committed.
  release: () =>
    set((prev) => {
      const editors = Math.max(prev.editors - 1, 0);
      return editors === 0 ? { editors, hydratedKey: null } : { editors };
    }),
}));
